/**
 * USB 利用ログ `/dlp/usb`。エージェントが report で送る着脱・遮断のイベント。
 * FilterBar(種別・端末・期間)+ DataTable + CSV(operator)。条件は URL に載せ、CSV にも同じ条件を渡す。
 */
import { Alert, Button, Select } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { DlpUsbEvent } from "../../lib/api";
import { jst } from "../../lib/format";
import {
  DataTable,
  FilterBar,
  PageHeader,
  PopClose,
  TimeAgo,
  dateRangeCondition,
  useSearchParamInput,
  useSearchParamsPatch,
  type ConditionDef,
  type DataColumn,
} from "../../ui";
import { deviceCell, usbKindTag } from "./bits";
import { USB_KINDS, usbKindLabel } from "./constants";
import { errorText, useDlpDownload, useDlpPerms, useDlpUsbEvents } from "./hooks";
import { DeviceSelect } from "./selects";

export function UsbEventsPage() {
  const { canOperate } = useDlpPerms();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const kind = sp.get("kind") ?? "";
  const deviceId = sp.get("device_id") ?? "";
  const since = sp.get("since") ?? "";
  const until = sp.get("until") ?? "";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const download = useDlpDownload();

  const filters: Record<string, string> = {
    ...(q ? { q } : {}),
    ...(kind ? { kind } : {}),
    ...(deviceId ? { device_id: deviceId } : {}),
    ...(since ? { since } : {}),
    ...(until ? { until } : {}),
  };
  const query = useDlpUsbEvents({ ...filters, limit: String(pageSize), offset: String((page - 1) * pageSize) });
  const filterKey = `${q}|${kind}|${deviceId}|${since}|${until}`;
  useEffect(() => setPage(1), [filterKey]);
  const rows = query.data?.items ?? [];

  const columns: DataColumn<DlpUsbEvent>[] = [
    {
      title: "日時",
      ellipsis: false,
      dataIndex: "at",
      width: 180,
      render: (v: string) => (
        <div>
          <TimeAgo iso={v} />
          <span className="ui-cell-sub ui-num">{jst(v)}</span>
        </div>
      ),
    },
    { title: "種別", ellipsis: false, width: 110, render: (_: unknown, r) => usbKindTag(r.kind) },
    { title: "端末", ellipsis: false, width: 190, render: (_: unknown, r) => deviceCell(r.device) },
    { title: "ユーザー", ellipsis: false, dataIndex: "username", width: 160 },
    {
      title: "ドライブ",
      ellipsis: false,
      width: 160,
      render: (_: unknown, r) => (
        <div>
          <span>{[r.drive, r.label].filter(Boolean).join(" ")}</span>
          {r.volume_serial && <span className="ui-cell-sub ui-num">{r.volume_serial}</span>}
        </div>
      ),
    },
    {
      title: "機器",
      ellipsis: false,
      width: 220,
      render: (_: unknown, r) => (
        <div>
          <span>{[r.vendor, r.product].filter(Boolean).join(" ")}</span>
          {r.device_serial && <span className="ui-cell-sub ui-num">{r.device_serial}</span>}
        </div>
      ),
    },
  ];

  const deviceLabel = deviceId ? (rows.find((r) => String(r.device_id) === deviceId)?.device?.hostname ?? `#${deviceId}`) : "";
  const conditions: ConditionDef[] = [
    {
      key: "kind",
      label: "種別",
      value: kind ? usbKindLabel(kind) : "",
      onClear: () => patch({ kind: null }, true),
      control: (close) => (
        <>
          <Select
            autoFocus
            style={{ minWidth: 160 }}
            value={kind || undefined}
            placeholder="種別"
            options={USB_KINDS}
            onChange={(v) => {
              patch({ kind: v ?? null }, true);
              close();
            }}
          />
          <PopClose onClick={close} />
        </>
      ),
    },
    {
      key: "device_id",
      label: "端末",
      value: deviceLabel,
      onClear: () => patch({ device_id: null }, true),
      control: (close) => (
        <>
          <DeviceSelect
            value={deviceId ? Number(deviceId) : null}
            onChange={(id) => {
              patch({ device_id: id ? String(id) : null }, true);
              if (id) close();
            }}
            autoFocus
          />
          <PopClose onClick={close} />
        </>
      ),
    },
    dateRangeCondition({
      key: "range",
      label: "期間",
      from: since,
      to: until,
      onChange: (from, to) => patch({ since: from || null, until: to || null }, true),
    }),
  ];
  const filtered = Object.keys(filters).length > 0;
  const clearAll = () => {
    setQInput("");
    patch({ q: null, kind: null, device_id: null, since: null, until: null }, true);
  };

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="USB 利用ログ"
          description="リムーバブルドライブの接続・取り外し・遮断。遮断は端末の USB モード(読み取り専用 / 遮断)が効いた印。中身(ファイル)は記録しない。"
          secondary={
            canOperate ? (
              <Button onClick={() => void download(`/api/dlp/usb/events.csv?${new URLSearchParams(filters).toString()}`)}>
                CSV
              </Button>
            ) : undefined
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "ホスト名 / ユーザー / ラベル / 機器" }}
          conditions={conditions}
          right={
            filtered ? (
              <Button size="small" onClick={clearAll}>
                条件を外す
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="USB イベントを取得できなかった" description={errorText(query.error)} />
        )}
        <DataTable<DlpUsbEvent>
          columns={columns}
          scrollX={columns.reduce((sum, c) => sum + c.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={query.data?.total ?? 0}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          rowTone={(row) => (row.kind === "blocked" ? "warning" : undefined)}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: filtered ? "条件に合うイベントはない" : "USB イベントはまだない。設定の「USB ログ」が有効なら、端末の次の report で届く",
            action: filtered ? <Button onClick={clearAll}>条件を外す</Button> : undefined,
          }}
        />
      </div>
    </div>
  );
}
